import { getRouteApi } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Loader2, MailCheck, XCircle, ShieldCheck, ArrowRight } from '@discovr/ui/icons';
import { waitlist } from '@discovr/api';

const waitlistVerifyRoute = getRouteApi('/waitlist/verify');

/**
 * Handles brand waitlist email verification links:
 *   /waitlist/verify?email=...&token=...
 *
 * Calls GET /api/v1/waitlist/verify (public endpoint) and shows
 * success or error state. No login CTA — brand cannot sign in until
 * admin explicitly approves their waitlist application.
 */
export default function WaitlistVerifyPage() {
  const { email = '', token = '' } = waitlistVerifyRoute.useSearch();
  const noParams = !email || !token;

  const { isPending, isSuccess, isError, error } = useQuery({
    queryKey: ['waitlist-verify', email, token],
    queryFn: () => waitlist.verify(email, token),
    enabled: !noParams,
    retry: 0,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const showLoading = !noParams && isPending;
  const showError = noParams || isError;
  const errorMsg = noParams
    ? 'This verification link is missing required parameters.'
    : (error as Error)?.message || 'This link is invalid or has expired.';

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-linear-to-br from-muted via-white to-indigo-50 px-4 py-12">
      {/* Card */}
      <div className="w-full max-w-md rounded-2xl border border-border bg-card  shadow-stone-200/60">
        {/* Header strip */}
        <div className="rounded-t-2xl bg-linear-to-r from-indigo-600 to-violet-600 px-8 py-6">
          <a href="/" className="flex items-center gap-2 text-white/90 hover:text-white">
            <span className="text-xl font-extrabold tracking-tight">Discovr</span>
          </a>
        </div>

        {/* Body */}
        <div className="p-8">
          {/* Loading */}
          {showLoading && (
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="flex size-20 items-center justify-center rounded-full bg-indigo-50">
                <Loader2 size={36} className="animate-spin text-indigo-500" />
              </div>
              <h1 className="text-heading-h6 text-foreground">Verifying your email…</h1>
              <p className="text-body-sm-regular text-muted-foreground">Hang tight, this only takes a second.</p>
            </div>
          )}

          {/* Success */}
          {isSuccess && (
            <div className="flex flex-col items-center gap-5 text-center">
              <div className="flex size-20 items-center justify-center rounded-full bg-emerald-50 ring-8 ring-emerald-50/50">
                <MailCheck size={36} className="text-emerald-500" />
              </div>
              <div>
                <h1 className="text-heading-h6 text-foreground">Email verified!</h1>
                <p className="mt-2 text-sm/relaxed text-muted-foreground">
                  Your email is confirmed. Our team will review your application and
                  send you a separate email with a link to set your password once
                  you're approved.
                </p>
              </div>
              {email && (
                <div className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-left">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Verified email
                  </p>
                  <p className="mt-0.5 text-body-sm-medium text-foreground">{email}</p>
                </div>
              )}
              <div className="flex items-center gap-2 text-body-sm-medium text-indigo-600">
                <span>What happens next?</span>
                <ArrowRight size={14} />
              </div>
              <ol className="w-full space-y-2 text-left text-body-sm-regular text-muted-foreground">
                {[
                  'Our team reviews your brand application',
                  'You receive an approval email with a set-password link',
                  'Set your password and access the Discovr dashboard',
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[11px] font-bold text-indigo-600">
                      {i + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Error */}
          {showError && (
            <div className="flex flex-col items-center gap-5 text-center">
              <div className="flex size-20 items-center justify-center rounded-full bg-rose-50 ring-8 ring-rose-50/50">
                <XCircle size={36} className="text-rose-500" />
              </div>
              <div>
                <h1 className="text-heading-h6 text-foreground">Verification failed</h1>
                <p className="mt-2 text-sm/relaxed text-muted-foreground">{errorMsg}</p>
              </div>
              <a
                href="/"
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-body-sm-semibold text-primary-foreground hover:bg-primary/90"
              >
                Back to homepage
              </a>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-start gap-3 rounded-b-2xl border-t border-border bg-muted px-8 py-4">
          <ShieldCheck size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
          <div className="text-xs/relaxed text-muted-foreground">
            <p>Verification links expire after 72 hours.</p>
            <p>
              Link expired?{' '}
              <a
                href="mailto:hello@getdiscovr.ai"
                className="font-semibold text-indigo-500 hover:text-indigo-600"
              >
                Contact support
              </a>{' '}
              and we'll send a fresh one.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
